/**
 * SuperadminFounders — Lista Founders Club + export CSV.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Crown, Download, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function SuperadminFounders() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [remaining, setRemaining] = useState(100);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        document.title = "Founders Club · Mission Control";
        (async () => {
            try {
                const { data } = await api.get("/superadmin/mc/founders");
                setItems(data.items || []);
                setTotal(data.total || 0);
                setRemaining(data.remaining_seats || 100);
            } catch (e) { setError(formatApiError(e)); }
            setLoading(false);
        })();
    }, []);

    return (
        <div className="min-h-screen px-6 py-8" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="superadmin-founders">
            <div className="max-w-6xl mx-auto">
                <Link to="/superadmin" className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-6" data-testid="back-mc">
                    <ArrowLeft size={12} /> Mission Control
                </Link>

                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg,#FFD66B,#FF6B3D)" }}>
                            <Crown size={18} color="#1a0c00" />
                        </div>
                        <div>
                            <h1 className="text-white text-[24px]" style={{ fontWeight: 400, letterSpacing: "-0.02em" }}>Founders Club</h1>
                            <div className="text-white/55 text-[12.5px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                {total} venduti · {remaining} posti rimasti · €{(total * 1990).toLocaleString("it-IT")} incassati
                            </div>
                        </div>
                    </div>
                    <a href="/api/superadmin/mc/founders/export.csv" target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12.5px] text-[#1a0c00]"
                        style={{ background: "linear-gradient(135deg,#FFD66B,#FF6B3D)", fontWeight: 600 }}
                        data-testid="export-csv">
                        <Download size={13} /> Esporta CSV
                    </a>
                </div>

                {error && <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

                <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <table className="w-full text-[13px]">
                        <thead>
                            <tr className="text-left text-[10.5px] uppercase tracking-[0.22em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                <th className="px-5 py-3.5">#</th>
                                <th className="px-5 py-3.5">Email</th>
                                <th className="px-5 py-3.5">Nome</th>
                                <th className="px-5 py-3.5">Provider</th>
                                <th className="px-5 py-3.5">Status</th>
                                <th className="px-5 py-3.5">Data</th>
                                <th className="px-5 py-3.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="7" className="px-5 py-8 text-center text-white/45 text-[12.5px]">Caricamento…</td></tr>}
                            {!loading && items.length === 0 && <tr><td colSpan="7" className="px-5 py-8 text-center text-white/45 text-[12.5px]">Nessun Founder ancora. La campagna parte: il counter pubblico mostra 36/100.</td></tr>}
                            {items.map((f) => (
                                <tr key={f.founder_number} className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`founder-row-${f.founder_number}`}>
                                    <td className="px-5 py-3.5">
                                        <span className="inline-block px-2 py-0.5 rounded text-[11px]" style={{ background: "linear-gradient(135deg,#FFD66B,#FF6B3D)", color: "#1a0c00", fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>
                                            #{String(f.founder_number).padStart(3, "0")}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3.5 text-white">{f.email || "—"}</td>
                                    <td className="px-5 py-3.5 text-white/75">{f.name || "—"}</td>
                                    <td className="px-5 py-3.5 text-white/55 uppercase text-[10.5px] tracking-[0.18em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{f.provider}</td>
                                    <td className="px-5 py-3.5">
                                        <span className="text-[10.5px] uppercase tracking-[0.22em] px-2 py-0.5 rounded" style={{ background: f.status === "active" ? "rgba(134,239,172,0.15)" : "rgba(255,255,255,0.05)", color: f.status === "active" ? "#86EFAC" : "#fff", fontFamily: "JetBrains Mono, monospace" }}>
                                            {f.status}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3.5 text-white/55 text-[11.5px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                        {f.created_at ? new Date(f.created_at).toLocaleDateString("it-IT") : "—"}
                                    </td>
                                    <td className="px-5 py-3.5 text-right">
                                        {f.email && (
                                            <a href={`mailto:${f.email}`} className="inline-flex items-center gap-1 text-[11.5px] text-white/55 hover:text-white">
                                                <Mail size={11} /> contatta
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
